import React, { useState, useEffect } from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { Button } from '../../components/ui/button';
import { Plus, Users, Search, Pencil, Trash2, Upload, FileSpreadsheet, X, UserPlus, Mail, Heart, AlertTriangle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const DailyGuestList = () => {
  const [guestList, setGuestList] = useState([]);
  const [filteredGuests, setFilteredGuests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [user, setUser] = useState(null);
  const [importing, setImporting] = useState(false);
  const [configuration, setConfiguration] = useState(null);
  const [isAssociateDialogOpen, setIsAssociateDialogOpen] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [associateEmail, setAssociateEmail] = useState('');
  const [associating, setAssociating] = useState(false);
  const [associatedProfiles, setAssociatedProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      fetchGuests(parsedUser.entityId);
      fetchConfiguration(parsedUser.entityId);
    }
  }, []);

  const fetchConfiguration = async (propertyId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/configuration/${propertyId}`);
      if (response.data.success) {
        setConfiguration(response.data.configuration);
      }
    } catch (error) {
      console.error('Error fetching configuration:', error);
    }
  };

  const fetchGuests = async (propertyId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/guests/${propertyId}`);
      if (response.data.success) {
        console.log('Fetched guests from backend:', response.data.guests);
        if (response.data.guests.length > 0) {
          console.log('First guest details:', {
            roomNumber: response.data.guests[0].roomNumber,
            guestName: response.data.guests[0].guestName,
            description: response.data.guests[0].description,
            checkInDate: response.data.guests[0].checkInDate,
            checkOutDate: response.data.guests[0].checkOutDate
          });
        }
        setGuestList(response.data.guests);
        setFilteredGuests(response.data.guests);
      }
    } catch (error) {
      console.error('Error fetching guests:', error);
      toast({
        title: "Error",
        description: "Failed to fetch guest list",
        variant: "destructive"
      });
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    
    if (term === '') {
      setFilteredGuests(guestList);
    } else {
      const filtered = guestList.filter(guest => 
        guest.guestName.toLowerCase().includes(term) ||
        guest.roomNumber.toLowerCase().includes(term) ||
        (guest.category && guest.category.toLowerCase().includes(term))
      );
      setFilteredGuests(filtered);
    }
  };

  const isGuestEligible = (guest) => {
    if (!guest.checkInDate || !guest.checkOutDate || !configuration) {
      return { eligible: true, reason: '' };
    }

    const now = new Date();
    const currentDate = now.toISOString().split('T')[0]; // YYYY-MM-DD
    const currentTime = now.toTimeString().slice(0, 5); // HH:MM

    const checkInDate = guest.checkInDate;
    const checkOutDate = guest.checkOutDate;
    const checkInTime = configuration.checkInTime || '14:00';
    const checkOutTime = configuration.checkOutTime || '11:00';

    // If current date is before check-in date
    if (currentDate < checkInDate) {
      return { eligible: false, reason: `Check-in is on ${new Date(checkInDate).toLocaleDateString()}` };
    }

    // If current date is the check-in date, check if it's past check-in time
    if (currentDate === checkInDate && currentTime < checkInTime) {
      return { eligible: false, reason: `Check-in time is ${checkInTime}` };
    }

    // If current date is after check-out date
    if (currentDate > checkOutDate) {
      return { eligible: false, reason: `Checked out on ${new Date(checkOutDate).toLocaleDateString()}` };
    }

    // If current date is the check-out date, check if it's past check-out time
    if (currentDate === checkOutDate && currentTime >= checkOutTime) {
      return { eligible: false, reason: `Check-out time was ${checkOutTime}` };
    }

    return { eligible: true, reason: '' };
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      // Validate and transform data
      const guests = jsonData.map(row => {
        // Extract dates - exact column names
        const checkInDateRaw = row['Checkin Date'] || null;
        const checkOutDateRaw = row['Checkout Date'] || null;
        
        // Format dates - handles Excel serial numbers and text formats
        const formatDate = (dateValue) => {
          if (!dateValue) return null;
          try {
            // Check if it's an Excel serial number (number type)
            if (typeof dateValue === 'number') {
              // Excel serial date: days since January 1, 1900
              // Note: Excel incorrectly treats 1900 as a leap year, so we adjust
              const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
              const date = new Date(excelEpoch.getTime() + dateValue * 86400000); // 86400000 ms in a day
              
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
            
            // Handle text format
            const dateString = String(dateValue).trim();
            
            if (!dateString) return null;
            
            // Check if it's in MM-DD-YYYY format
            if (dateString.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
              const [month, day, year] = dateString.split('-');
              return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
            
            // Try parsing as a date
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
          } catch (e) {
            console.error('Invalid date:', dateValue);
          }
          return null;
        };

        const guest = {
          roomNumber: String(row['Room Number'] || '').trim(),
          guestName: String(row['Guest Name'] || '').trim(),
          category: (row['Category'] && String(row['Category']).trim()) || null,
          description: (row['Description'] && String(row['Description']).trim()) || null,
          checkInDate: formatDate(checkInDateRaw),
          checkOutDate: formatDate(checkOutDateRaw)
        };

        // Debug log for first guest
        if (guest.roomNumber === '101') {
          console.log('Sample guest data:', guest);
          console.log('Raw check-in:', checkInDateRaw);
          console.log('Raw check-out:', checkOutDateRaw);
        }

        return guest;
      }).filter(g => g.roomNumber && g.guestName);

      if (guests.length === 0) {
        toast({
          title: "Invalid File",
          description: "No valid guest data found. Please check the file format.",
          variant: "destructive"
        });
        setImporting(false);
        return;
      }

      // Send to backend
      const response = await axios.post(`${BACKEND_URL}/api/guests/bulk`, {
        propertyId: user.entityId,
        guests: guests
      });

      if (response.data.success) {
        toast({
          title: "Success",
          description: `Successfully imported ${response.data.count} guests`
        });
        fetchGuests(user.entityId);
      }
    } catch (error) {
      console.error('Error importing guests:', error);
      toast({
        title: "Import Failed",
        description: error.response?.data?.detail || "Failed to import guest list",
        variant: "destructive"
      });
    } finally {
      setImporting(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear all guests? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await axios.delete(`${BACKEND_URL}/api/guests/property/${user.entityId}`);
      
      if (response.data.success) {
        toast({
          title: "Success",
          description: `Cleared ${response.data.count} guests`
        });
        fetchGuests(user.entityId);
      }
    } catch (error) {
      console.error('Error clearing guests:', error);
      toast({
        title: "Error",
        description: "Failed to clear guest list",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (guestId) => {
    if (!window.confirm('Are you sure you want to delete this guest?')) {
      return;
    }

    try {
      const response = await axios.delete(`${BACKEND_URL}/api/guests/${guestId}`);
      
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Guest deleted successfully"
        });
        fetchGuests(user.entityId);
      }
    } catch (error) {
      console.error('Error deleting guest:', error);
      toast({
        title: "Error",
        description: "Failed to delete guest",
        variant: "destructive"
      });
    }
  };

  const handleOpenAssociateDialog = async (guest) => {
    setSelectedGuest(guest);
    setAssociateEmail('');
    setAssociatedProfiles([]);
    setIsAssociateDialogOpen(true);
    
    // Fetch associated profiles for this guest
    setLoadingProfiles(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/guests/${guest.id}/associated-guest-profiles`
      );
      if (response.data.success) {
        setAssociatedProfiles(response.data.guestProfiles);
      }
    } catch (error) {
      console.error('Error fetching associated profiles:', error);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const handleAssociateGuest = async () => {
    if (!associateEmail || !selectedGuest) return;
    
    setAssociating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/guests/${selectedGuest.id}/associate-guest-profile`,
        { email: associateEmail }
      );
      
      if (response.data.success) {
        toast({
          title: "Success",
          description: response.data.message
        });
        setAssociateEmail('');
        // Refresh the associated profiles list
        const profilesResponse = await axios.get(
          `${BACKEND_URL}/api/guests/${selectedGuest.id}/associated-guest-profiles`
        );
        if (profilesResponse.data.success) {
          setAssociatedProfiles(profilesResponse.data.guestProfiles);
        }
        fetchGuests(user.entityId);
      }
    } catch (error) {
      console.error('Error associating guest:', error);
      const errorMessage = error.response?.data?.detail || "Failed to associate guest profile";
      toast({
        title: "Error",
        description: errorMessage === "No guest profile found with this email" 
          ? "Guest Profile doesn't exist" 
          : errorMessage,
        variant: "destructive"
      });
    } finally {
      setAssociating(false);
    }
  };

  const handleRemoveAssociation = async (profileId) => {
    if (!selectedGuest) return;
    
    try {
      const response = await axios.delete(
        `${BACKEND_URL}/api/guests/${selectedGuest.id}/associated-guest-profiles/${profileId}`
      );
      
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Guest profile association removed"
        });
        // Refresh the associated profiles list
        setAssociatedProfiles(prev => prev.filter(p => p.id !== profileId));
        fetchGuests(user.entityId);
      }
    } catch (error) {
      console.error('Error removing association:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to remove association",
        variant: "destructive"
      });
    }
  };

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Daily Guest List</h1>
            <p className="text-slate-600 mt-1">Manage your property's guest list</p>
          </div>
          <div className="flex gap-2">
            {guestList.length > 0 && (
              <Button 
                onClick={handleClearAll}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            )}
            <Button 
              onClick={() => document.getElementById('excel-upload').click()}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
              disabled={importing}
            >
              {importing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import from Excel
                </>
              )}
            </Button>
            <input
              id="excel-upload"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        </div>

        {/* Instructions Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-1">Excel File Format</p>
              <p className="text-sm text-blue-800 mb-2">
                Your Excel file must have these exact column names:
              </p>
              <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
                <li><strong>Room Number</strong> (required)</li>
                <li><strong>Guest Name</strong> (required)</li>
                <li><strong>Category</strong> (optional)</li>
                <li><strong>Description</strong> (optional)</li>
                <li><strong>Checkin Date</strong> (MM-DD-YYYY format, e.g., 12-27-2024)</li>
                <li><strong>Checkout Date</strong> (MM-DD-YYYY format, e.g., 12-30-2024)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        {guestList.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by guest name, room number, or category..."
                className="pl-10"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        )}

        {/* Guest List */}
        {filteredGuests.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12">
            <div className="text-center max-w-md mx-auto">
              <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-12 h-12 text-slate-400" />
              </div>
              <h2 className="text-xl font-semibold text-slate-800 mb-2">
                {searchTerm ? 'No guests found' : 'No guests yet'}
              </h2>
              <p className="text-slate-600 mb-6">
                {searchTerm 
                  ? 'Try adjusting your search terms'
                  : 'Import your guest list from an Excel file to get started.'}
              </p>
              {!searchTerm && (
                <Button 
                  onClick={() => document.getElementById('excel-upload').click()}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import from Excel
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Room Number</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Guest Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Category</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Description</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Check-in</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Check-out</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Profiles</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Status</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredGuests.map((guest) => {
                    const eligibility = isGuestEligible(guest);
                    return (
                      <tr key={guest.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-semibold text-slate-900">{guest.roomNumber}</td>
                        <td className="px-6 py-4 text-sm text-slate-900">{guest.guestName}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {guest.category ? (
                            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-semibold">
                              {guest.category}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {guest.description ? (
                            <span className="text-slate-700">{guest.description}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {guest.checkInDate ? (
                            new Date(guest.checkInDate).toLocaleDateString()
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {guest.checkOutDate ? (
                            new Date(guest.checkOutDate).toLocaleDateString()
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {(guest.associatedGuestProfileIds?.length || 0) > 0 ? (
                            <button
                              onClick={() => handleOpenAssociateDialog(guest)}
                              className="inline-flex items-center px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-semibold hover:bg-teal-200 transition-colors cursor-pointer"
                            >
                              <Users className="w-3 h-3 mr-1" />
                              {guest.associatedGuestProfileIds.length} Profile{guest.associatedGuestProfileIds.length > 1 ? 's' : ''} Associated
                            </button>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {eligibility.eligible ? (
                            <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                              ✓ Can allocate
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold" title={eligibility.reason}>
                              ✗ Not eligible
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleOpenAssociateDialog(guest)}
                              title="Associate Guest Profile"
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDelete(guest.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                Showing <strong>{filteredGuests.length}</strong> of <strong>{guestList.length}</strong> guests
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Associate Guest Profile Dialog */}
      <Dialog open={isAssociateDialogOpen} onOpenChange={setIsAssociateDialogOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Manage Guest Profile Associations</DialogTitle>
            <DialogDescription>
              Room {selectedGuest?.roomNumber} - {selectedGuest?.guestName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Associated Profiles List */}
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Associated Guest Profiles ({associatedProfiles.length})
              </Label>
              
              {loadingProfiles ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-2 text-sm text-slate-500">Loading...</span>
                </div>
              ) : associatedProfiles.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {associatedProfiles.map((profile) => (
                    <div
                      key={profile.id}
                      className="flex items-center justify-between bg-slate-50 rounded-lg p-3 border border-slate-200"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-xs">
                            {profile.firstName?.charAt(0)}{profile.lastName?.charAt(0) || ''}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 text-sm">
                            {profile.firstName} {profile.lastName || ''}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Mail className="w-3 h-3" />
                            <span>{profile.email}</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                        onClick={() => handleRemoveAssociation(profile.id)}
                        title="Remove association"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                  <Users className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">No guest profiles associated yet</p>
                </div>
              )}
            </div>

            {/* Add New Association */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">
                Add New Association
              </Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter guest profile email"
                  value={associateEmail}
                  onChange={(e) => setAssociateEmail(e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={handleAssociateGuest}
                  disabled={!associateEmail || associating}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {associating ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <UserPlus className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Email must match an existing guest profile
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsAssociateDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </UserLayout>
  );
};
